import React from 'react';
import { ChevronDown, LucideIcon } from 'lucide-react';

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'compact' | 'chip';
  options?: Array<{ value: string | number; label: string }>;
  icon?: LucideIcon;
}

/**
 * Select Component v2 - FacturXPro Design System
 *
 * Select dropdown avec glassmorphism, focus ring Teal et icône chevron
 *
 * @example
 * ```tsx
 * // Avec options array
 * <Select
 *   label="Statut"
 *   options={[
 *     { value: 'draft', label: 'Brouillon' },
 *     { value: 'sent', label: 'Envoyé' },
 *   ]}
 *   value={status}
 *   onChange={(e) => setStatus(e.target.value)}
 * />
 *
 * // Avec children JSX
 * <Select value={status} onChange={handleChange}>
 *   <option value="draft">Brouillon</option>
 *   <option value="sent">Envoyé</option>
 * </Select>
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Inputs - Select"
 */
export const Select: React.FC<SelectProps> = ({
  label,
  error,
  helperText,
  fullWidth = true,
  size = 'md',
  variant = 'default',
  options,
  icon: Icon,
  className = '',
  children,
  ...props
}) => {
  const sizeClasses = {
    sm: 'px-2.5 py-1.5 text-xs pr-8',
    md: 'px-3 py-2 text-sm pr-9',
    lg: 'px-4 py-2.5 text-base pr-10',
  };

  // Variant classes avec glassmorphism
  const variantClasses = {
    default: 'bg-white/40 backdrop-blur-sm border-white/40 rounded-xl shadow-sm hover:bg-white/60 focus:bg-white/70',
    compact: 'bg-white/30 backdrop-blur-sm border-gray-200 rounded-lg hover:bg-white/50 focus:bg-white/60',
    chip: 'bg-white/30 backdrop-blur-sm border-transparent rounded-xl hover:bg-white/50 focus:bg-white/60 border-2',
  };

  const widthClasses = fullWidth ? 'w-full' : '';

  const errorClasses = error
    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
    : 'border focus:ring-2 focus:ring-teal-500 focus:border-teal-500';

  return (
    <div className={widthClasses}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          className={`
            ${sizeClasses[size]}
            ${variantClasses[variant]}
            ${errorClasses}
            ${widthClasses}
            border
            transition-all
            duration-300
            outline-none
            appearance-none
            font-medium
            text-gray-900
            cursor-pointer
            ${className}
          `.trim()}
          {...props}
        >
          {/* Si children est fourni, l'utiliser, sinon utiliser options */}
          {children
            ? children
            : options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
        </select>
        {Icon ? (
          <Icon className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
        ) : (
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        )}
      </div>
      {error && <p className="mt-1.5 text-sm text-red-600 font-medium">{error}</p>}
      {helperText && !error && (
        <p className="mt-1.5 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
};

export default Select;
